import { APP_INITIALIZER, ApplicationConfig, inject, provideBrowserGlobalErrorListeners, provideZoneChangeDetection } from '@angular/core';
import { provideRouter, Router } from '@angular/router';

import { routes } from './app.routes';
import { provideHttpClient, withInterceptorsFromDi, HTTP_INTERCEPTORS } from '@angular/common/http';
import { AuthInterceptor } from './authentication/interceptors/auth.interceptor';
import { KeyCloakCustomService } from './keycloak/services/key-cloak-custom-service';
import { ErrorInterceptor } from './authentication/interceptors/error.interceptor';
// Runtime check (no DI)
const IS_BROWSER = typeof window !== 'undefined' && typeof document !== 'undefined';

// Initialize KeyCloak Custom Service
const initializeKeycloak = async (keycloakService: KeyCloakCustomService): Promise<void> => {
  try {
    if (!IS_BROWSER) {
      console.log('SSR environment - skipping Keycloak initialization');
      return;
    }

    console.log('Initializing KeyCloak Custom Service...');
    const isAuthenticated = await keycloakService.initialize();

    if (isAuthenticated) {
      console.log('User is authenticated from existing session');
      await keycloakService.loadUserProfile();
    } else {
      console.log('No existing session found - user will need to login');
    }
  } catch (error) {
    console.error('Failed to initialize KeyCloak Custom Service:', error);
  }
};

// Auth guard for protected routes
export const canActivateAuth = async (): Promise<boolean> => {
  if (!IS_BROWSER) return true; // SSR: allow render

  const keycloakService = inject(KeyCloakCustomService);
  const router = inject(Router);

  const isAuthenticated = keycloakService.isLoggedIn();

  if (!isAuthenticated) {
    const returnUrl = router.routerState.snapshot.url;
    router.navigate(['/login'], {
      queryParams: { returnUrl },
      queryParamsHandling: 'merge'
    });
    return false;
  }

  return true;
};

// HTTP Client with interceptors
const httpClientProviders = [
  provideHttpClient(
    withInterceptorsFromDi()
  ), 
  {
    provide: HTTP_INTERCEPTORS,
    useClass: AuthInterceptor,
    multi: true
  },
  {
    provide: HTTP_INTERCEPTORS,
    useClass: ErrorInterceptor,
    multi: true
  }
  // {
  //   provide: SECURE_STORAGE_CONFIG,
  //   useValue: {
  //     backend: 'local',                 // 'session' | 'memory'
  //     namespace: 'myapp:',
  //     appSecret: 'PUT_A_32+_CHAR_RANDOM_SECRET_HERE_rotate_periodically'
  //   }
  // }
];

// Browser-specific providers
const browserProviders = [
  // KeyCloak Custom Service
  KeyCloakCustomService,
  {
    provide: APP_INITIALIZER,
    useFactory: (keycloakService: KeyCloakCustomService) => () => initializeKeycloak(keycloakService),
    multi: true,
    deps: [KeyCloakCustomService]
  },
  ...httpClientProviders
];

// SSR-specific providers  
const ssrProviders = [
  KeyCloakCustomService,
  ...httpClientProviders
];
export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    ...(IS_BROWSER ? browserProviders : ssrProviders)
  ]
};
